/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { Box, Typography, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import { Iconify } from 'src/components';

const WIDTH = 800;
const HEIGHT = 250;
const PADDING = { top: 20, right: 40, bottom: 40, left: 60 };
const CHART_WIDTH = WIDTH - PADDING.left - PADDING.right;
const CHART_HEIGHT = HEIGHT - PADDING.top - PADDING.bottom;

export default function LostLeadsTrend({ data }) {
    const maxValue = useMemo(() => Math.max(...data.map((d) => d.count || 0), 1), [data]);

    const points = useMemo(() => {
        if (data.length === 0) return [];
        const xStep = CHART_WIDTH / (data.length - 1 || 1);
        return data.map((d, i) => {
            const x = PADDING.left + i * xStep;
            const y = PADDING.top + CHART_HEIGHT - ((d.count || 0) / maxValue) * CHART_HEIGHT;
            return { x, y, value: d.count, label: d.label };
        });
    }, [data, maxValue]);

    const linePath = useMemo(() => {
        if (points.length === 0) return '';
        return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    }, [points]);

    const areaPath = useMemo(() => {
        if (points.length === 0) return '';
        const linePoints = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
        return `${linePoints} L ${points[points.length - 1]?.x} ${PADDING.top + CHART_HEIGHT} L ${PADDING.left} ${PADDING.top + CHART_HEIGHT} Z`;
    }, [points]);

    const yLabels = useMemo(() => {
        const steps = 4;
        return Array.from({ length: steps + 1 }, (_, i) => {
            const value = Math.round((maxValue * (steps - i)) / steps);
            const y = PADDING.top + (CHART_HEIGHT * i) / steps;
            return { value, y };
        });
    }, [maxValue]);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                mb: 3,
            }}
        >
            <Tooltip title="Monthly trend showing how many leads were lost over time." placement="bottom" arrow
                slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help', mb: 3 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>Lost Leads Trend</Typography>
                    <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                </Box>
            </Tooltip>

            {data.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>No trend data available</Typography>
                </Box>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ width: '100%', maxHeight: '250px' }}>
                        <defs>
                            <linearGradient id="lostLeadsGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
                                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.02" />
                            </linearGradient>
                        </defs>

                        {yLabels.map((label) => (
                            <g key={label.y}>
                                <line x1={PADDING.left} y1={label.y} x2={WIDTH - PADDING.right} y2={label.y} stroke="#f1f5f9" strokeWidth="1" />
                                <text x={PADDING.left - 10} y={label.y + 4} textAnchor="end" fontSize="11" fill="#94a3b8">
                                    {label.value}
                                </text>
                            </g>
                        ))}

                        {areaPath && <path d={areaPath} fill="url(#lostLeadsGradient)" />}
                        {linePath && <path d={linePath} fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}

                        {points.map((point) => (
                            <g key={`${point.x}-${point.y}`}>
                                <circle cx={point.x} cy={point.y} r="4" fill="#fff" stroke="#ef4444" strokeWidth="2" />
                                <text x={point.x} y={HEIGHT - 8} textAnchor="middle" fontSize="11" fill="#64748b">
                                    {point.label}
                                </text>
                            </g>
                        ))}
                    </svg>
                </Box>
            )}
        </Box>
    );
}

LostLeadsTrend.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        month: PropTypes.string,
        label: PropTypes.string,
        count: PropTypes.number,
    })),
};

LostLeadsTrend.defaultProps = {
    data: [],
};
